import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BrandCalendarIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {rpAppLink} from "@pg-mono/rp-routes";
import {formatDate} from "@pg-mono/string-utils";

import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useResponsiveLinkTarget} from "../../hooks/useResponsiveLinkTarget";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {hitPromotionBoxClick, hitPromotionBoxView} from "../../tracking/google_tag_manager/gtm_promotion_events";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {getPromotionListingConfig, getPromotionListingDefaultMessage} from "../helpers/promotion_listing_config";
import {IPromotionListPromotion} from "../interfaces/IPromotionListResponse";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";

interface IPromotionBox {
    position: number;
    promotion: IPromotionListPromotion;
}

export const PromotionBox = (props: IPromotionBox) => {
    const {position, promotion} = props;
    const config = getPromotionListingConfig(promotion.type);
    const offerUrl = `${rpAppLink.offer.detail.base({
        vendorSlug: promotion.offer.vendor.slug,
        offerSlug: promotion.offer.slug,
        offerId: promotion.offer.id
    })}#promocje`;
    const promotionTypeLabel = config.shortLabel;
    const promotionHeadline = `${promotion.offer.name}: ${promotionTypeLabel}`;
    const promotionPanelTitle = promotion.name || config.fullLabel;
    const availablePropertiesCount = promotion.offer.stats?.properties_count_for_sale;
    const secondaryLinkLabel =
        typeof availablePropertiesCount === "number" && availablePropertiesCount > 0
            ? `Zobacz mieszkania (${availablePropertiesCount})`
            : "Zobacz szczegóły promocji";
    const dateBadgeLabel = getPromotionDateBadgeLabel(promotion.promotion_date_range);
    const vendorLogoUrl = getThumbnailLink(promotion.offer.vendor.logo_url, 120, 90) ?? getVendorLogoUrl(promotion.offer.vendor.logo);
    const mainImage = promotion.offer.main_image;
    const promotionImageSrc = getThumbnailLink(promotion.offer.main_image_url, 760, 428) ?? mainImage.m_img_760x428 ?? mainImage.m_img_375x211;
    const promotionImageSrc2x = getThumbnailLink(promotion.offer.main_image_url, 1500) ?? mainImage.m_img_1500;
    const responsiveLinkTarget = useResponsiveLinkTarget();
    const trackingElementId = `promotion-box-${promotion.id}-${position}`;
    const trackingPayload = {
        promotionCategory: config.fullLabel,
        developerName: promotion.offer.vendor.name,
        offerName: promotion.offer.name,
        position: position.toString()
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: config.ctaLabel,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: promotion.offer.vendor.name,
        offerName: promotion.offer.name
    };

    const amplitudeLeadFormTrackingContext = getAmplitudePromotionLeadFormTrackingContext({
        promotionTrackingContext: getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId: promotion.offer.id,
            creativeSlot: "promotion_box"
        }),
        applicationSource: ApplicationSource.Promotion,
        ctaText: config.ctaLabel
    });

    useElementWasVisible({
        ids: [trackingElementId],
        once: true,
        threshold: 0.05,
        onElementVisible: () => {
            hitPromotionBoxView(trackingPayload);
        }
    });

    const trackBoxClick = (clickText: string) => {
        hitPromotionBoxClick({
            ...trackingPayload,
            clickText
        });
    };

    return (
        <li className={promotionBoxHolder} data-id={trackingElementId}>
            <a href={offerUrl} className={imageLinkStyle} aria-label={promotionHeadline} onClick={() => trackBoxClick("promotion_box")}>
                <Image
                    className={promotionBoxImageWrapperStyle}
                    src={promotionImageSrc}
                    src2x={promotionImageSrc2x}
                    alt={promotion.offer.name}
                    width="760"
                    height="428"
                    imageClassName={promotionBoxImageStyle}
                />
            </a>

            <div className={contentStyle}>
                <div className={headerStyle}>
                    <div className={titleLocationWrapperStyle}>
                        <a href={offerUrl} className={titleLinkStyle} onClick={() => trackBoxClick("promotion_box")}>
                            <Text as="h2" variant="headline_6" className={titleStyle}>
                                <span>{promotion.offer.name}: </span>
                                <Text as="span" variant="headline_6" strong>
                                    <Highlight>{promotionTypeLabel}</Highlight>
                                </Text>
                            </Text>
                        </a>

                        <div className={locationStyle}>
                            <MapMarkerOutlineIcon size="1.6" className={locationIconStyle} />
                            <Text as="span" variant="info_txt_2" className={locationTextStyle}>
                                {promotion.offer.address}
                            </Text>
                        </div>
                    </div>

                    {vendorLogoUrl && (
                        <div className={vendorLogoWrapperStyle}>
                            <img src={vendorLogoUrl} alt={`${promotion.offer.vendor.name} logo`} className={vendorLogoStyle} loading="lazy" decoding="async" />
                        </div>
                    )}
                </div>

                <div className={promotionDetailsStyle}>
                    <Text as="p" variant="headline_6" strong className={promotionTitleStyle}>
                        {promotionPanelTitle}
                    </Text>

                    {dateBadgeLabel && (
                        <div className={dateBadgeStyle}>
                            <BrandCalendarIcon size="1.6" className={dateBadgeIconStyle} />
                            <Text as="span" variant="info_txt_2" strong>
                                {dateBadgeLabel}
                            </Text>
                        </div>
                    )}

                    {promotion.description && (
                        <Text as="p" variant="body_copy_2" className={descriptionStyle}>
                            {promotion.description}
                        </Text>
                    )}

                    <div className={actionsStyle}>
                        <ApplicationModalButton
                            flowVariant="promotion"
                            initialStep={ApplicationModalStep.PROMOTION_FORM}
                            source={ApplicationSource.Promotion}
                            sourceSection={ApplicationSourceSection.BODY}
                            topic={{
                                message: getPromotionListingDefaultMessage(promotion.type, promotion.offer.name)
                            }}
                            vendorId={promotion.offer.vendor.id}
                            offerId={promotion.offer.id}
                            propertyId={null}
                            render={({openModal}) => (
                                <Button
                                    onClick={() => {
                                        trackBoxClick(config.ctaLabel);
                                        openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
                                    }}
                                    size="small"
                                    type="button"
                                    variant="filled_primary"
                                    className={promotionActionButtonStyle}
                                >
                                    {config.ctaLabel}
                                </Button>
                            )}
                        />

                        <Button
                            href={offerUrl}
                            onClick={() => trackBoxClick(secondaryLinkLabel)}
                            size="small"
                            target={responsiveLinkTarget}
                            variant="outlined_secondary"
                            className={promotionActionButtonStyle}
                        >
                            {secondaryLinkLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </li>
    );
};

const getPromotionDateBadgeLabel = (dateRange: IPromotionListPromotion["promotion_date_range"]): string | null => {
    if (!dateRange?.lower && !dateRange?.upper) {
        return null;
    }

    if (dateRange.lower && dateRange.upper) {
        return `od ${formatDate(dateRange.lower, "dd.MM.yyyy")} do ${formatDate(dateRange.upper, "dd.MM.yyyy")}`;
    }

    if (dateRange.upper) {
        return `do ${formatDate(dateRange.upper, "dd.MM.yyyy")}`;
    }

    return `od ${formatDate(dateRange.lower, "dd.MM.yyyy")}`;
};

const getVendorLogoUrl = (logo: IPromotionListPromotion["offer"]["vendor"]["logo"]): string | null => {
    return logo?.v_log_120x90 || logo?.v_log_160x120 || logo?.v_log_80x60 || null;
};

const promotionBoxHolder = css({
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "stretch",
    width: "100%",
    overflow: "hidden",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    md: {
        gridTemplateColumns: "43.1rem minmax(0, 1fr)",
        minHeight: "33.8rem"
    }
});

const imageLinkStyle = css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "16.2rem",
    color: "inherit",
    textDecoration: "none",
    backgroundColor: "var(--colors-gray-200)",
    md: {
        alignSelf: "stretch",
        height: "100%",
        minHeight: "27.4rem"
    }
});

const promotionBoxImageWrapperStyle = css({
    display: "block",
    width: "100%",
    height: "100%"
});

const promotionBoxImageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    p: 1.5,
    md: {
        p: 2
    }
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    width: "100%"
});

const titleLocationWrapperStyle = css({
    display: "flex",
    flex: "1 1 0",
    flexDirection: "column",
    gap: 1,
    minWidth: 0
});

const titleLinkStyle = css({
    color: "var(--colors-secondary)"
});

const titleStyle = css({
    color: "inherit",
    wordBreak: "break-word"
});

const locationStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const locationIconStyle = css({
    flex: "0 0 auto",
    fill: "var(--colors-secondary)"
});

const locationTextStyle = css({
    color: "inherit",
    wordBreak: "break-word"
});

const vendorLogoWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flex: "0 0 auto",
    width: "7.2rem",
    minWidth: "7.2rem",
    height: "5.4rem"
});

const vendorLogoStyle = css({
    display: "block",
    maxWidth: "7.2rem",
    maxHeight: "5.4rem",
    width: "100%",
    height: "100%",
    objectFit: "contain"
});

const promotionDetailsStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    width: "100%",
    p: 1,
    borderRadius: 0.5,
    backgroundColor: "var(--colors-background-100)"
});

const promotionTitleStyle = css({
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const dateBadgeStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    flex: "0 0 auto",
    minHeight: "2.4rem",
    px: 1,
    borderRadius: 1,
    backgroundColor: "var(--colors-gray-200)",
    color: "var(--colors-secondary)"
});

const dateBadgeIconStyle = css({
    flex: "0 0 auto",
    fill: "var(--colors-secondary)"
});

const descriptionStyle = css({
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const actionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",
    mt: "auto",
    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const promotionActionButtonStyle = css({
    width: "100%",
    whiteSpace: "nowrap",
    md: {
        width: "auto",
        minWidth: "20rem"
    }
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BrandCalendarIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {rpAppLink} from "@pg-mono/rp-routes";
import {formatDate} from "@pg-mono/string-utils";

import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useResponsiveLinkTarget} from "../../hooks/useResponsiveLinkTarget";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {hitPromotionBoxClick, hitPromotionBoxView} from "../../tracking/google_tag_manager/gtm_promotion_events";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {getPromotionListingConfig, getPromotionListingDefaultMessage} from "../helpers/promotion_listing_config";
import {IPromotionListPromotion} from "../interfaces/IPromotionListResponse";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";

interface IPromotionBox {
    position: number;
    promotion: IPromotionListPromotion;
}

export const PromotionBox = (props: IPromotionBox) => {
    const {position, promotion} = props;
    const config = getPromotionListingConfig(promotion.type);
    const offerUrl = `${rpAppLink.offer.detail.base({
        vendorSlug: promotion.offer.vendor.slug,
        offerSlug: promotion.offer.slug,
        offerId: promotion.offer.id
    })}#promocje`;
    const promotionTypeLabel = config.shortLabel;
    const promotionHeadline = `${promotion.offer.name}: ${promotionTypeLabel}`;
    const promotionPanelTitle = promotion.name || config.fullLabel;
    const availablePropertiesCount = promotion.offer.stats?.properties_count_for_sale;
    const secondaryLinkLabel =
        typeof availablePropertiesCount === "number" && availablePropertiesCount > 0
            ? `Zobacz mieszkania (${availablePropertiesCount})`
            : "Zobacz szczegóły promocji";
    const dateBadgeLabel = getPromotionDateBadgeLabel(promotion.promotion_date_range);
    const vendorLogoUrl = getThumbnailLink(promotion.offer.vendor.logo_url, 120, 90) ?? getVendorLogoUrl(promotion.offer.vendor.logo);
    const mainImage = promotion.offer.main_image;
    const promotionImageSrc = getThumbnailLink(promotion.offer.main_image_url, 760, 428) ?? mainImage.m_img_760x428 ?? mainImage.m_img_375x211;
    const promotionImageSrc2x = getThumbnailLink(promotion.offer.main_image_url, 1500) ?? mainImage.m_img_1500;
    const responsiveLinkTarget = useResponsiveLinkTarget();
    const trackingElementId = `promotion-box-${promotion.id}-${position}`;
    const trackingPayload = {
        promotionCategory: config.fullLabel,
        developerName: promotion.offer.vendor.name,
        offerName: promotion.offer.name,
        position: position.toString()
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: config.ctaLabel,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: promotion.offer.vendor.name,
        offerName: promotion.offer.name
    };

    const amplitudeLeadFormTrackingContext = getAmplitudePromotionLeadFormTrackingContext({
        promotionTrackingContext: getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId: promotion.offer.id,
            creativeSlot: "promotion_box"
        }),
        applicationSource: ApplicationSource.Promotion,
        ctaText: config.ctaLabel
    });

    useElementWasVisible({
        ids: [trackingElementId],
        once: true,
        threshold: 0.05,
        onElementVisible: () => {
            hitPromotionBoxView(trackingPayload);
        }
    });

    const trackBoxClick = (clickText: string) => {
        hitPromotionBoxClick({
            ...trackingPayload,
            clickText
        });
    };

    return (
        <li className={promotionBoxHolder} data-id={trackingElementId}>
            <a href={offerUrl} className={imageLinkStyle} aria-label={promotionHeadline} onClick={() => trackBoxClick("promotion_box")}>
                <Image
                    className={promotionBoxImageWrapperStyle}
                    src={promotionImageSrc}
                    src2x={promotionImageSrc2x}
                    alt={promotion.offer.name}
                    width="760"
                    height="428"
                    imageClassName={promotionBoxImageStyle}
                />
            </a>

            <div className={contentStyle}>
                <div className={headerStyle}>
                    <div className={titleLocationWrapperStyle}>
                        <a href={offerUrl} className={titleLinkStyle} onClick={() => trackBoxClick("promotion_box")}>
                            <Text as="h2" variant="headline_6" className={titleStyle}>
                                <span>{promotion.offer.name}: </span>
                                <Text as="span" variant="headline_6" strong>
                                    <Highlight>{promotionTypeLabel}</Highlight>
                                </Text>
                            </Text>
                        </a>

                        <div className={locationStyle}>
                            <MapMarkerOutlineIcon size="1.6" className={locationIconStyle} />
                            <Text as="span" variant="info_txt_2" className={locationTextStyle}>
                                {promotion.offer.address}
                            </Text>
                        </div>
                    </div>

                    {vendorLogoUrl && (
                        <div className={vendorLogoWrapperStyle}>
                            <img src={vendorLogoUrl} alt={`${promotion.offer.vendor.name} logo`} className={vendorLogoStyle} loading="lazy" decoding="async" />
                        </div>
                    )}
                </div>

                <div className={promotionDetailsStyle}>
                    <Text as="p" variant="headline_6" strong className={promotionTitleStyle}>
                        {promotionPanelTitle}
                    </Text>

                    {dateBadgeLabel && (
                        <div className={dateBadgeStyle}>
                            <BrandCalendarIcon size="1.6" className={dateBadgeIconStyle} />
                            <Text as="span" variant="info_txt_2" strong>
                                {dateBadgeLabel}
                            </Text>
                        </div>
                    )}

                    {promotion.description && (
                        <Text as="p" variant="body_copy_2" className={descriptionStyle}>
                            {promotion.description}
                        </Text>
                    )}

                    <div className={actionsStyle}>
                        <ApplicationModalButton
                            flowVariant="promotion"
                            initialStep={ApplicationModalStep.PROMOTION_FORM}
                            source={ApplicationSource.Promotion}
                            sourceSection={ApplicationSourceSection.BODY}
                            topic={{
                                message: getPromotionListingDefaultMessage(promotion.type, promotion.offer.name)
                            }}
                            vendorId={promotion.offer.vendor.id}
                            offerId={promotion.offer.id}
                            propertyId={null}
                            render={({openModal}) => (
                                <Button
                                    onClick={() => {
                                        trackBoxClick(config.ctaLabel);
                                        openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
                                    }}
                                    size="small"
                                    type="button"
                                    variant="filled_primary"
                                    className={promotionActionButtonStyle}
                                >
                                    {config.ctaLabel}
                                </Button>
                            )}
                        />

                        <Button
                            href={offerUrl}
                            onClick={() => trackBoxClick(secondaryLinkLabel)}
                            size="small"
                            target={responsiveLinkTarget}
                            variant="outlined_secondary"
                            className={promotionActionButtonStyle}
                        >
                            {secondaryLinkLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </li>
    );
};

const getPromotionDateBadgeLabel = (dateRange: IPromotionListPromotion["promotion_date_range"]): string | null => {
    if (!dateRange?.lower && !dateRange?.upper) {
        return null;
    }

    if (dateRange.lower && dateRange.upper) {
        return `od ${formatDate(dateRange.lower, "dd.MM.yyyy")} do ${formatDate(dateRange.upper, "dd.MM.yyyy")}`;
    }

    if (dateRange.upper) {
        return `do ${formatDate(dateRange.upper, "dd.MM.yyyy")}`;
    }

    return `od ${formatDate(dateRange.lower, "dd.MM.yyyy")}`;
};

const getVendorLogoUrl = (logo: IPromotionListPromotion["offer"]["vendor"]["logo"]): string | null => {
    return logo?.v_log_120x90 || logo?.v_log_160x120 || logo?.v_log_80x60 || null;
};

const promotionBoxHolder = css({
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "stretch",
    width: "100%",
    overflow: "hidden",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    md: {
        gridTemplateColumns: "43.1rem minmax(0, 1fr)",
        minHeight: "33.8rem"
    }
});

const imageLinkStyle = css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "16.2rem",
    color: "inherit",
    textDecoration: "none",
    backgroundColor: "var(--colors-gray-200)",
    md: {
        alignSelf: "stretch",
        height: "100%",
        minHeight: "27.4rem"
    }
});

const promotionBoxImageWrapperStyle = css({
    display: "block",
    width: "100%",
    height: "100%"
});

const promotionBoxImageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    p: 1.5,
    md: {
        p: 2
    }
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    width: "100%"
});

const titleLocationWrapperStyle = css({
    display: "flex",
    flex: "1 1 0",
    flexDirection: "column",
    gap: 1,
    minWidth: 0
});

const titleLinkStyle = css({
    color: "var(--colors-secondary)"
});

const titleStyle = css({
    color: "inherit",
    wordBreak: "break-word"
});

const locationStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const locationIconStyle = css({
    flex: "0 0 auto",
    fill: "var(--colors-secondary)"
});

const locationTextStyle = css({
    color: "inherit",
    wordBreak: "break-word"
});

const vendorLogoWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flex: "0 0 auto",
    width: "7.2rem",
    minWidth: "7.2rem",
    height: "5.4rem"
});

const vendorLogoStyle = css({
    display: "block",
    maxWidth: "7.2rem",
    maxHeight: "5.4rem",
    width: "100%",
    height: "100%",
    objectFit: "contain"
});

const promotionDetailsStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    width: "100%",
    p: 1,
    borderRadius: 0.5,
    backgroundColor: "var(--colors-background-100)"
});

const promotionTitleStyle = css({
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const dateBadgeStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    flex: "0 0 auto",
    minHeight: "2.4rem",
    px: 1,
    borderRadius: 1,
    backgroundColor: "var(--colors-gray-200)",
    color: "var(--colors-secondary)"
});

const dateBadgeIconStyle = css({
    flex: "0 0 auto",
    fill: "var(--colors-secondary)"
});

const descriptionStyle = css({
    color: "var(--colors-secondary)",
    wordBreak: "break-word"
});

const actionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",
    mt: "auto",
    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const promotionActionButtonStyle = css({
    width: "100%",
    whiteSpace: "nowrap",
    md: {
        width: "auto",
        minWidth: "20rem"
    }
});
